import React, { useState, useEffect, useRef } from 'react'
import { Modal, Button, Form, Row, Col, Tabs, Tab, InputGroup } from 'react-bootstrap'
import { EditorBlock, BlockConfigData } from './types'
import { MediaManagerModal } from '../MediaManagerModal'
import { HTMLEditor } from '../HTMLEditor'

const EMOJI_CATEGORIES = [
  { label: 'Cobertura', emojis: ['🛡️','🔒','🔐','🏥','🚗','🏠','🏢','✈️','🌊','🔑'] },
  { label: 'Beneficios', emojis: ['✅','☑️','⭐','🌟','🎯','💡','🏆','🥇','🎖️','👍'] },
  { label: 'Finanzas',   emojis: ['💰','💵','💳','📊','📈','💹','🏦','💎','🪙','📉'] },
  { label: 'Personas',   emojis: ['🤝','👥','👤','👨‍💼','👩‍💼','👨‍⚕️','👩‍⚕️','🧑‍💻','👪','🫂'] },
  { label: 'Salud',      emojis: ['❤️','🩺','💊','🩹','🧬','🫀','🧠','🩻','🌡️','💪'] },
  { label: 'Redes Sociales', emojis: ['📘','📸','💼','▶️','🎵','💬','✈️','🌐','@','#'] },
  { label: 'Otros',      emojis: ['🔔','⚡','🌈','🎁','📍','⏰','📞','✉️','💬','📋'] },
]

interface EmojiPickerProps {
  value: string
  onChange: (emoji: string) => void
}

const EmojiPicker: React.FC<EmojiPickerProps> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          width: 56, height: 56, fontSize: 28,
          border: '1px solid #dee2e6', borderRadius: 8,
          background: '#f8f9fa', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
        title="Seleccionar emoji"
      >
        {value || '✅'}
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 1050,
          background: '#fff', border: '1px solid #dee2e6', borderRadius: 10,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)', padding: 12, width: 280,
          marginTop: 4,
        }}>
          {EMOJI_CATEGORIES.map(cat => (
            <div key={cat.label} className="mb-2">
              <div style={{ fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', marginBottom: 4 }}>
                {cat.label}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {cat.emojis.map(emoji => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => { onChange(emoji); setOpen(false) }}
                    style={{
                      width: 36, height: 36, fontSize: 20,
                      border: value === emoji ? '2px solid #007bff' : '1px solid transparent',
                      borderRadius: 6, background: 'transparent', cursor: 'pointer',
                    }}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

interface BlockConfigurationModalProps {
    show: boolean
    onHide: () => void
    block: EditorBlock | null
    initialData?: BlockConfigData
    onConfirm: (data: BlockConfigData) => void
}

interface PxInputProps {
    value?: string
    onChange: (value: string) => void
    placeholder?: string
    min?: number
    size?: 'sm' | 'lg'
}

const toPxNumber = (value?: string): string => {
    if (!value) return ''
    return String(value).replace(/[^\d]/g, '')
}

const PxInput: React.FC<PxInputProps> = ({ value, onChange, placeholder = '0', min = 0, size }) => {
    return (
        <InputGroup size={size}>
            <Form.Control
                type="number"
                min={min}
                step={1}
                value={toPxNumber(value)}
                onChange={(e) => {
                    const raw = e.target.value.replace(/[^\d]/g, '')
                    onChange(raw ? `${raw}px` : '')
                }}
                placeholder={placeholder}
            />
            <InputGroup.Text>px</InputGroup.Text>
        </InputGroup>
    )
}

interface HexColorInputProps {
    value?: string
    onChange: (value: string) => void
    fallback?: string
    size?: 'sm' | 'lg'
}

const normalizeHexColor = (value?: string, fallback = '#000000'): string => {
    if (!value || value === 'transparent') return fallback

    const trimmed = value.trim()
    const withHash = trimmed.startsWith('#') ? trimmed : `#${trimmed}`
    const hex = withHash.slice(1)

    if (/^[0-9a-fA-F]{6}$/.test(hex)) return `#${hex.toUpperCase()}`
    if (/^[0-9a-fA-F]{3}$/.test(hex)) {
        return `#${hex.split('').map((char) => `${char}${char}`).join('').toUpperCase()}`
    }

    return fallback
}

const HexColorInput: React.FC<HexColorInputProps> = ({ value, onChange, fallback = '#000000', size }) => {
    const normalizedValue = normalizeHexColor(value, fallback)

    return (
        <div className="d-flex gap-2 align-items-center">
            <Form.Control
                type="color"
                value={normalizedValue}
                onChange={(e) => onChange(e.target.value.toUpperCase())}
                title="Select color"
                style={{ width: '50px' }}
            />
            <Form.Control
                size={size}
                type="text"
                value={value === 'transparent' ? '' : normalizedValue}
                onChange={(e) => onChange(e.target.value)}
                onBlur={(e) => onChange(normalizeHexColor(e.target.value, fallback))}
                placeholder={fallback}
            />
        </div>
    )
}

export const BlockConfigurationModal: React.FC<BlockConfigurationModalProps> = ({
    show,
    onHide,
    block,
    initialData,
    onConfirm,
}) => {
    const [data, setData] = useState<BlockConfigData>({})
    const [showMediaManager, setShowMediaManager] = useState(false)
    const [mediaTarget, setMediaTarget] = useState<'imageUrl' | 'logoUrl' | 'quoteAvatarUrl' | 'card1Image' | 'card2Image' | 'card3Image' | null>(null)
    const [activeTab, setActiveTab] = useState<string>('content')
    const [contentEditorField, setContentEditorField] = useState<keyof BlockConfigData | null>(null)
    const [contentEditorLabel, setContentEditorLabel] = useState('')

    // Reset data when block changes
    useEffect(() => {
        if (show && block) {
            // Start with initialData if provided, otherwise use defaults
            const baseData = initialData || {}
            const mergedData: BlockConfigData = {
                ...baseData,
                align: baseData.align || 'center',
                padding: baseData.padding || '20px',
                blockSpacing: baseData.blockSpacing || '20px',
                border: baseData.border,
                buttonColor: baseData.buttonColor || '#007bff',
                buttonText: baseData.buttonText ?? '',
                title: baseData.title ?? '',
                subtitle: baseData.subtitle ?? '',
                content: baseData.content ?? '',
                backgroundColor: baseData.backgroundColor || '#f8f9fa',
                paddingTop: baseData.paddingTop || '20px',
                paddingBottom: baseData.paddingBottom || '20px',
                paddingLeft: baseData.paddingLeft || '20px',
                paddingRight: baseData.paddingRight || '20px',
                buttonPaddingVertical: baseData.buttonPaddingVertical || '12px',
                buttonPaddingHorizontal: baseData.buttonPaddingHorizontal || '25px',
                // Preserve column content
                col1Content: baseData.col1Content,
                col2Content: baseData.col2Content,
                col3Content: baseData.col3Content,
                // Preserve column widths
                col1Width: baseData.col1Width,
                col2Width: baseData.col2Width,
                col3Width: baseData.col3Width,
                // Preserve column styling
                col1BackgroundColor: baseData.col1BackgroundColor,
                col2BackgroundColor: baseData.col2BackgroundColor,
                col3BackgroundColor: baseData.col3BackgroundColor,
                col1Padding: baseData.col1Padding,
                col2Padding: baseData.col2Padding,
                col3Padding: baseData.col3Padding,
                col1Border: baseData.col1Border,
                col2Border: baseData.col2Border,
                col3Border: baseData.col3Border,
            }
            setData(mergedData)
        }
    }, [show, block, initialData])

    const handleChange = (field: keyof BlockConfigData, value: any) => {
        setData((prev) => ({ ...prev, [field]: value }))
    }

    const handleMediaSelect = (url: string) => {
        if (mediaTarget) {
            handleChange(mediaTarget as keyof BlockConfigData, url)
        }
        setShowMediaManager(false)
        setMediaTarget(null)
    }

    const openMediaFor = (target: 'imageUrl' | 'logoUrl' | 'quoteAvatarUrl' | 'card1Image' | 'card2Image' | 'card3Image') => {
        setMediaTarget(target)
        setShowMediaManager(true)
    }

    const openContentEditor = (field: keyof BlockConfigData, label: string) => {
        setContentEditorField(field)
        setContentEditorLabel(label)
    }

    const renderFields = () => {
        if (!block) return null

        switch (block.id) {
            case 'hero':
                return (
                    <>
                        <Form.Group className="mb-3">
                            <Form.Label>Hero Image</Form.Label>
                            <div className="d-flex gap-2">
                                <Form.Control type="text" value={data.imageUrl || ''} onChange={e => handleChange('imageUrl', e.target.value)} placeholder="https://..." />
                                <Button variant="outline-secondary" onClick={() => openMediaFor('imageUrl')}>Select</Button>
                            </div>
                        </Form.Group>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Image Width</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={data.imageWidth || ''}
                                        onChange={e => handleChange('imageWidth', e.target.value)}
                                        placeholder="e.g., 600px, 100%"
                                    />
                                    <Form.Text>Leave empty for full width</Form.Text>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Image Height</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={data.imageHeight || ''}
                                        onChange={e => handleChange('imageHeight', e.target.value)}
                                        placeholder="e.g., 300px, auto"
                                    />
                                    <Form.Text>Leave empty for auto</Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Group className="mb-3">
                            <Form.Label>Title</Form.Label>
                            <Form.Control type="text" value={data.title || ''} onChange={e => handleChange('title', e.target.value)} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Subtitle</Form.Label>
                            <Form.Control as="textarea" rows={2} value={data.subtitle || ''} onChange={e => handleChange('subtitle', e.target.value)} />
                        </Form.Group>
                        <Row>
                            <Col>
                                <Form.Group className="mb-3">
                                    <Form.Label>Button Text</Form.Label>
                                    <Form.Control type="text" value={data.buttonText || ''} onChange={e => handleChange('buttonText', e.target.value)} />
                                </Form.Group>
                            </Col>
                            <Col>
                                <Form.Group className="mb-3">
                                    <Form.Label>Button URL</Form.Label>
                                    <Form.Control type="text" value={data.buttonUrl || ''} onChange={e => handleChange('buttonUrl', e.target.value)} />
                                </Form.Group>
                            </Col>
                        </Row>
                    </>
                )

            case 'image_text':
                return (
                    <>
                        {/* Image Position */}
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold">📐 Column Image Position</Form.Label>
                            <div className="d-flex gap-2 mt-1">
                                <Button
                                    size="sm"
                                    variant={(!data.imagePosition || data.imagePosition === 'left') ? 'primary' : 'outline-secondary'}
                                    onClick={() => handleChange('imagePosition', 'left')}
                                    style={{ minWidth: 90 }}
                                >
                                    ⬅ Left
                                </Button>
                                <Button
                                    size="sm"
                                    variant={data.imagePosition === 'right' ? 'primary' : 'outline-secondary'}
                                    onClick={() => handleChange('imagePosition', 'right')}
                                    style={{ minWidth: 90 }}
                                >
                                    Right ➡
                                </Button>
                            </div>
                        </Form.Group>

                        {/* Image URL */}
                        <Form.Group className="mb-3">
                            <Form.Label>🖼 Image</Form.Label>
                            <div className="d-flex gap-2">
                                <Form.Control type="text" value={data.imageUrl || ''} onChange={e => handleChange('imageUrl', e.target.value)} placeholder="https://..." />
                                <Button variant="outline-secondary" onClick={() => openMediaFor('imageUrl')}>Select</Button>
                            </div>
                        </Form.Group>

                        {/* Image Alt */}
                        <Form.Group className="mb-3">
                            <Form.Label>Alt Text</Form.Label>
                            <Form.Control
                                type="text"
                                value={data.imageAlt || ''}
                                onChange={e => handleChange('imageAlt', e.target.value)}
                                placeholder="Describe la imagen..."
                            />
                        </Form.Group>

                        {/* Image Size */}
                        <Row>
                            <Col md={12} className="mb-3">
                                <Form.Group>
                                    <Form.Label>
                                        📏 Col. Width: <strong>{data.imageWidth || '40%'}</strong>
                                    </Form.Label>
                                    <Form.Range
                                        min={20}
                                        max={80}
                                        step={5}
                                        value={parseInt(data.imageWidth as string) || 40}
                                        onChange={e => handleChange('imageWidth', `${e.target.value}%`)}
                                    />
                                    <div className="d-flex justify-content-between">
                                        <Form.Text>20%</Form.Text>
                                        <Form.Text>50%</Form.Text>
                                        <Form.Text>80%</Form.Text>
                                    </div>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Image Into Column</Form.Label>
                                    <Form.Select value={data.align || 'center'} onChange={e => handleChange('align', e.target.value)}>
                                        <option value="left">Left</option>
                                        <option value="center">Center</option>
                                        <option value="right">Right</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Image Height</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={data.imageHeight || ''}
                                        onChange={e => handleChange('imageHeight', e.target.value)}
                                        placeholder="e.g. 200px, auto"
                                    />
                                    <Form.Text>Leave empty for auto</Form.Text>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Border Radius</Form.Label>
                                    <PxInput
                                        value={data.imageBorderRadius || '4px'}
                                        onChange={value => handleChange('imageBorderRadius', value)}
                                        placeholder="4"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* Text Content */}
                        <Form.Group className="mb-3">
                            <Form.Label>Heading</Form.Label>
                            <Form.Control type="text" value={data.title || ''} onChange={e => handleChange('title', e.target.value)} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <Form.Label className="mb-0">Content</Form.Label>
                                <Button variant="outline-secondary" size="sm" onClick={() => openContentEditor('content', 'Content')}>
                                    <i className="bi bi-pencil-square me-1"></i>
                                    Edit Content
                                </Button>
                            </div>
                            <Form.Control as="textarea" rows={3} value={data.content || ''} onChange={e => handleChange('content', e.target.value)} />
                        </Form.Group>
                        <Row>
                            <Col>
                                <Form.Group className="mb-3">
                                    <Form.Label>CTA Text (Optional)</Form.Label>
                                    <Form.Control type="text" value={data.buttonText || ''} onChange={e => handleChange('buttonText', e.target.value)} />
                                </Form.Group>
                            </Col>
                            <Col>
                                <Form.Group className="mb-3">
                                    <Form.Label>CTA URL</Form.Label>
                                    <Form.Control type="text" value={data.buttonUrl || ''} onChange={e => handleChange('buttonUrl', e.target.value)} />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>CTA Background Color</Form.Label>
                                    <div className="d-flex gap-2 mb-2">
                                        <Form.Control
                                            type="color"
                                            value={data.buttonColor || '#007bff'}
                                            onChange={e => handleChange('buttonColor', e.target.value)}
                                            style={{ width: '60px' }}
                                        />
                                        <Form.Control
                                            type="text"
                                            value={data.buttonColor || '#007bff'}
                                            onChange={e => handleChange('buttonColor', e.target.value)}
                                            placeholder="#007bff"
                                        />
                                    </div>
                                    <Form.Check
                                        type="switch"
                                        id="cta-outline-switch"
                                        label="Outline Style"
                                        checked={data.buttonStyle === 'outline'}
                                        onChange={e => handleChange('buttonStyle', e.target.checked ? 'outline' : 'solid')}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </>
                )


            case 'button':
                return (
                    <>
                        <Form.Group className="mb-3">
                            <Form.Label>Lead Title (Optional)</Form.Label>
                            <Form.Control
                                type="text"
                                value={data.title || ''}
                                onChange={e => handleChange('title', e.target.value)}
                                placeholder="e.g., ¡Oferta Especial!"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Lead Text (Optional)</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={data.content || ''}
                                onChange={e => handleChange('content', e.target.value)}
                                placeholder="Texto llamativo antes del botón..."
                            />
                        </Form.Group>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Button Text</Form.Label>
                                    <Form.Control type="text" value={data.buttonText || ''} onChange={e => handleChange('buttonText', e.target.value)} />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>URL</Form.Label>
                                    <Form.Control type="text" value={data.buttonUrl || ''} onChange={e => handleChange('buttonUrl', e.target.value)} />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Text Color</Form.Label>
                                    <Form.Control type="color" value={data.color || '#ffffff'} onChange={e => handleChange('color', e.target.value)} />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Button Color</Form.Label>
                                    <Form.Control type="color" value={data.buttonColor || '#007bff'} onChange={e => handleChange('buttonColor', e.target.value)} title="Choose button color" />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Button Width</Form.Label>
                                    <Form.Control type="text" value={data.buttonWidth || ''} onChange={e => handleChange('buttonWidth', e.target.value)} placeholder="e.g., 200px, 100%, auto" />
                                    <Form.Text className="text-muted">Dejar vacío para ancho automático basado en el texto</Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Border Radius</Form.Label>
                                    <PxInput value={data.borderRadius || '5px'} onChange={value => handleChange('borderRadius', value)} placeholder="5" />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Font Size</Form.Label>
                                    <PxInput value={data.fontSize || '16px'} onChange={value => handleChange('fontSize', value)} placeholder="16" />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Button Placement (when centered)</Form.Label>
                                    <div>
                                        <Form.Check
                                            inline
                                            label="Above Text"
                                            type="radio"
                                            id="placement-above"
                                            checked={data.buttonPlacement === 'above'}
                                            onChange={() => handleChange('buttonPlacement', 'above')}
                                        />
                                        <Form.Check
                                            inline
                                            label="Below Text"
                                            type="radio"
                                            id="placement-below"
                                            checked={(data.buttonPlacement || 'below') === 'below'}
                                            onChange={() => handleChange('buttonPlacement', 'below')}
                                        />
                                    </div>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Padding Vertical</Form.Label>
                                    <PxInput value={data.buttonPaddingVertical || '12px'} onChange={value => handleChange('buttonPaddingVertical', value)} placeholder="12" />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Padding Horizontal</Form.Label>
                                    <PxInput value={data.buttonPaddingHorizontal || '25px'} onChange={value => handleChange('buttonPaddingHorizontal', value)} placeholder="25" />
                                </Form.Group>
                            </Col>
                        </Row>
                    </>
                )

            case 'icon_note':
                return (
                    <>
                        <Form.Group className="mb-4">
                            <Form.Label>
                                Icon Column Width: <strong>{data.iconWidth || '18%'}</strong>
                            </Form.Label>
                            <Form.Range
                                min={10}
                                max={40}
                                step={2}
                                value={parseInt(data.iconWidth as string) || 18}
                                onChange={e => handleChange('iconWidth', `${e.target.value}%`)}
                            />
                            <div className="d-flex justify-content-between">
                                <Form.Text>10%</Form.Text>
                                <Form.Text>25%</Form.Text>
                                <Form.Text>40%</Form.Text>
                            </div>
                        </Form.Group>

                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Icon</Form.Label>
                                    <EmojiPicker
                                        value={data.iconSymbol || '⚠️'}
                                        onChange={(emoji) => handleChange('iconSymbol', emoji)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Icon Color</Form.Label>
                                    <HexColorInput
                                        value={data.iconColor || '#F59E0B'}
                                        onChange={value => handleChange('iconColor', value)}
                                        fallback='#F59E0B'
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Icon Size</Form.Label>
                                    <PxInput value={data.iconSize || '34px'} onChange={value => handleChange('iconSize', value)} placeholder="34" />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group className="mb-3">
                            <Form.Label>Title</Form.Label>
                            <Form.Control
                                type="text"
                                value={data.title || ''}
                                onChange={e => handleChange('title', e.target.value)}
                                placeholder="Recuerda:"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <Form.Label className="mb-0">Text</Form.Label>
                                <Button variant="outline-secondary" size="sm" onClick={() => openContentEditor('content', 'Text')}>
                                    <i className="bi bi-pencil-square me-1"></i>
                                    Edit Content
                                </Button>
                            </div>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={data.content || ''}
                                onChange={e => handleChange('content', e.target.value)}
                                placeholder="No compartas tus datos por teléfono."
                            />
                        </Form.Group>

                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Title Color</Form.Label>
                                    <HexColorInput
                                        value={data.titleColor || '#1D4F91'}
                                        onChange={value => handleChange('titleColor', value)}
                                        fallback='#1D4F91'
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Text Color</Form.Label>
                                    <HexColorInput
                                        value={data.contentColor || '#1F2937'}
                                        onChange={value => handleChange('contentColor', value)}
                                        fallback='#1F2937'
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Background</Form.Label>
                                    <HexColorInput
                                        value={data.backgroundColor || '#FFF8DB'}
                                        onChange={value => handleChange('backgroundColor', value)}
                                        fallback='#FFF8DB'
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </>
                )

            case 'image':
                return (
                    <>
                        <Form.Group className="mb-3">
                            <Form.Label>Image Source</Form.Label>
                            <div className="d-flex gap-2">
                                <Form.Control type="text" value={data.imageUrl || ''} onChange={e => handleChange('imageUrl', e.target.value)} placeholder="https://..." />
                                <Button variant="outline-secondary" onClick={() => openMediaFor('imageUrl')}>Select</Button>
                            </div>
                            <Form.Text>Choose from Media Manager or paste URL</Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Alt Text (for accessibility)</Form.Label>
                            <Form.Control
                                type="text"
                                value={data.imageAlt || ''}
                                onChange={e => handleChange('imageAlt', e.target.value)}
                                placeholder="Describe the image..."
                            />
                        </Form.Group>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Width</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={data.imageWidth || ''}
                                        onChange={e => handleChange('imageWidth', e.target.value)}
                                        placeholder="e.g., 300px, 100%, auto"
                                    />
                                    <Form.Text>Leave empty for default (100%)</Form.Text>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Height</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={data.imageHeight || ''}
                                        onChange={e => handleChange('imageHeight', e.target.value)}
                                        placeholder="e.g., 200px, auto"
                                    />
                                    <Form.Text>Leave empty for auto</Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Border Radius</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={data.borderRadius || '0px'}
                                        onChange={e => handleChange('borderRadius', e.target.value)}
                                        placeholder="e.g., 8px, 50%"
                                    />
                                    <Form.Text>Use 50% for circular images</Form.Text>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Alignment</Form.Label>
                                    <Form.Select value={data.align || 'center'} onChange={e => handleChange('align', e.target.value)}>
                                        <option value="left">Left</option>
                                        <option value="center">Center</option>
                                        <option value="right">Right</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                    </>
                )

            case '1_col':
            case '2_cols':
            case '3_cols':
                return (
                    <>
                        <Form.Group className="mb-3">
                            <Form.Label>Background Color</Form.Label>
                            <div className="d-flex align-items-center gap-2">
                                <Form.Control
                                    type="color"
                                    value={data.backgroundColor || '#ffffff'}
                                    onChange={e => handleChange('backgroundColor', e.target.value)}
                                    title="Choose color"
                                    className="form-control-color"
                                />
                                <Form.Control
                                    type="text"
                                    value={data.backgroundColor || '#ffffff'}
                                    onChange={e => handleChange('backgroundColor', e.target.value)}
                                />
                            </div>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Padding (e.g., 20px 0)</Form.Label>
                            <Form.Control
                                type="text"
                                value={data.padding || '20px 0'}
                                onChange={e => handleChange('padding', e.target.value)}
                                placeholder="10px 10px 10px 10px"
                            />
                        </Form.Group>

                        {/* Block Spacing */}
                        <Form.Group className="mb-3">
                            <Form.Label>Espaciado Inferior del Bloque</Form.Label>
                            <Form.Control
                                type="text"
                                value={data.blockSpacing || '20px'}
                                onChange={e => handleChange('blockSpacing', e.target.value)}
                                placeholder="20px"
                            />
                            <Form.Text className="text-muted">
                                Espacio después de este bloque. Usa "0px" para pegar bloques.
                            </Form.Text>
                        </Form.Group>

                        {/* Alignment for 1_col */}
                        {block.id === '1_col' && (
                            <Form.Group className="mb-3">
                                <Form.Label>📐 Content Alignment</Form.Label>
                                <Form.Select value={data.align || 'left'} onChange={e => handleChange('align', e.target.value)}>
                                    <option value="left">⬅ Left</option>
                                    <option value="center">↔ Center</option>
                                    <option value="right">➡ Right</option>
                                </Form.Select>
                            </Form.Group>
                        )}

                        {/* Border */}
                        <Form.Group className="mb-3">
                            <Form.Label>Borde del Bloque</Form.Label>
                            <Form.Control
                                type="text"
                                value={data.border || ''}
                                onChange={e => handleChange('border', e.target.value)}
                                placeholder="1px solid #dddddd"
                            />
                            <Form.Text className="text-muted">
                                Ejemplos: "1px solid #ddd", "2px dashed #000", "" (sin borde)
                            </Form.Text>
                        </Form.Group>

                        {/* Column Width Configuration */}
                        {block.id === '2_cols' && (
                            <>
                                <hr className="my-3" />
                                <h6 className="mb-3">📐 Ancho de Columnas</h6>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Columna 1 (Ancho)</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={data.col1Width || '450px'}
                                                onChange={e => handleChange('col1Width', e.target.value)}
                                                placeholder="450px"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Columna 2 (Ancho)</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={data.col2Width || '450px'}
                                                onChange={e => handleChange('col2Width', e.target.value)}
                                                placeholder="450px"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <hr className="my-3" />
                                <h6 className="mb-3">🎨 Estilo por Columna</h6>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Col 1 - Background</Form.Label>
                                            <div className="d-flex gap-2">
                                                <Form.Control
                                                    type="color"
                                                    value={data.col1BackgroundColor || '#ffffff'}
                                                    onChange={e => handleChange('col1BackgroundColor', e.target.value)}
                                                    style={{ width: '60px' }}
                                                />
                                                <Form.Control
                                                    type="text"
                                                    value={data.col1BackgroundColor || '#ffffff'}
                                                    onChange={e => handleChange('col1BackgroundColor', e.target.value)}
                                                    placeholder="#ffffff"
                                                />
                                            </div>
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Col 1 - Padding</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={data.col1Padding || '30px'}
                                                onChange={e => handleChange('col1Padding', e.target.value)}
                                                placeholder="30px"
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Col 1 - Borde</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={data.col1Border || ''}
                                                onChange={e => handleChange('col1Border', e.target.value)}
                                                placeholder="1px solid #ddd"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Col 2 - Background</Form.Label>
                                            <div className="d-flex gap-2">
                                                <Form.Control
                                                    type="color"
                                                    value={data.col2BackgroundColor || '#ffffff'}
                                                    onChange={e => handleChange('col2BackgroundColor', e.target.value)}
                                                    style={{ width: '60px' }}
                                                />
                                                <Form.Control
                                                    type="text"
                                                    value={data.col2BackgroundColor || '#ffffff'}
                                                    onChange={e => handleChange('col2BackgroundColor', e.target.value)}
                                                    placeholder="#ffffff"
                                                />
                                            </div>
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Col 2 - Padding</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={data.col2Padding || '30px'}
                                                onChange={e => handleChange('col2Padding', e.target.value)}
                                                placeholder="30px"
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Col 2 - Borde</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={data.col2Border || ''}
                                                onChange={e => handleChange('col2Border', e.target.value)}
                                                placeholder="1px solid #ddd"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <hr className="my-3" />
                                <h6 className="mb-3">↔ Alineación por Columna</h6>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="small">Col 1 - Align</Form.Label>
                                            <Form.Select size="sm" value={data.col1Align || 'left'} onChange={e => handleChange('col1Align', e.target.value)}>
                                                <option value="left">⬅ Left</option>
                                                <option value="center">↔ Center</option>
                                                <option value="right">➡ Right</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="small">Col 2 - Align</Form.Label>
                                            <Form.Select size="sm" value={data.col2Align || 'left'} onChange={e => handleChange('col2Align', e.target.value)}>
                                                <option value="left">⬅ Left</option>
                                                <option value="center">↔ Center</option>
                                                <option value="right">➡ Right</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <div className="alert alert-info small mb-0">
                                    <strong>💡 Tip:</strong> Usa diferentes colores de fondo para crear diseños visuales interesantes (ej: gris + blanco)
                                </div>
                            </>
                        )}

                        {block.id === '3_cols' && (
                            <>
                                <hr className="my-3" />
                                <h6 className="mb-3">📐 Ancho de Columnas</h6>
                                <Row>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Col 1</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={data.col1Width || '300px'}
                                                onChange={e => handleChange('col1Width', e.target.value)}
                                                placeholder="300px"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Col 2</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={data.col2Width || '300px'}
                                                onChange={e => handleChange('col2Width', e.target.value)}
                                                placeholder="300px"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Col 3</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={data.col3Width || '300px'}
                                                onChange={e => handleChange('col3Width', e.target.value)}
                                                placeholder="300px"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <hr className="my-3" />
                                <h6 className="mb-3">🎨 Estilo por Columna</h6>
                                <Row>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="small">Col 1 - BG</Form.Label>
                                            <div className="d-flex gap-1">
                                                <Form.Control
                                                    type="color"
                                                    value={data.col1BackgroundColor || '#ffffff'}
                                                    onChange={e => handleChange('col1BackgroundColor', e.target.value)}
                                                    style={{ width: '50px' }}
                                                />
                                                <Form.Control
                                                    type="text"
                                                    value={data.col1BackgroundColor || '#ffffff'}
                                                    onChange={e => handleChange('col1BackgroundColor', e.target.value)}
                                                    placeholder="#fff"
                                                    size="sm"
                                                />
                                            </div>
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="small">Padding</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={data.col1Padding || '10px'}
                                                onChange={e => handleChange('col1Padding', e.target.value)}
                                                placeholder="10px"
                                                size="sm"
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="small">Borde</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={data.col1Border || ''}
                                                onChange={e => handleChange('col1Border', e.target.value)}
                                                placeholder="1px solid #ddd"
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="small">Col 2 - BG</Form.Label>
                                            <div className="d-flex gap-1">
                                                <Form.Control
                                                    type="color"
                                                    value={data.col2BackgroundColor || '#ffffff'}
                                                    onChange={e => handleChange('col2BackgroundColor', e.target.value)}
                                                    style={{ width: '50px' }}
                                                />
                                                <Form.Control
                                                    type="text"
                                                    value={data.col2BackgroundColor || '#ffffff'}
                                                    onChange={e => handleChange('col2BackgroundColor', e.target.value)}
                                                    placeholder="#fff"
                                                    size="sm"
                                                />
                                            </div>
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="small">Padding</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={data.col2Padding || '10px'}
                                                onChange={e => handleChange('col2Padding', e.target.value)}
                                                placeholder="10px"
                                                size="sm"
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="small">Borde</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={data.col2Border || ''}
                                                onChange={e => handleChange('col2Border', e.target.value)}
                                                placeholder="1px solid #ddd"
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="small">Col 3 - BG</Form.Label>
                                            <div className="d-flex gap-1">
                                                <Form.Control
                                                    type="color"
                                                    value={data.col3BackgroundColor || '#ffffff'}
                                                    onChange={e => handleChange('col3BackgroundColor', e.target.value)}
                                                    style={{ width: '50px' }}
                                                />
                                                <Form.Control
                                                    type="text"
                                                    value={data.col3BackgroundColor || '#ffffff'}
                                                    onChange={e => handleChange('col3BackgroundColor', e.target.value)}
                                                    placeholder="#fff"
                                                    size="sm"
                                                />
                                            </div>
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="small">Padding</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={data.col3Padding || '10px'}
                                                onChange={e => handleChange('col3Padding', e.target.value)}
                                                placeholder="10px"
                                                size="sm"
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="small">Borde</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={data.col3Border || ''}
                                                onChange={e => handleChange('col3Border', e.target.value)}
                                                placeholder="1px solid #ddd"
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <hr className="my-3" />
                                <h6 className="mb-3">↔ Alineación por Columna</h6>
                                <Row>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="small">Col 1 - Align</Form.Label>
                                            <Form.Select size="sm" value={data.col1Align || 'left'} onChange={e => handleChange('col1Align', e.target.value)}>
                                                <option value="left">⬅ Left</option>
                                                <option value="center">↔ Center</option>
                                                <option value="right">➡ Right</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="small">Col 2 - Align</Form.Label>
                                            <Form.Select size="sm" value={data.col2Align || 'left'} onChange={e => handleChange('col2Align', e.target.value)}>
                                                <option value="left">⬅ Left</option>
                                                <option value="center">↔ Center</option>
                                                <option value="right">➡ Right</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="small">Col 3 - Align</Form.Label>
                                            <Form.Select size="sm" value={data.col3Align || 'left'} onChange={e => handleChange('col3Align', e.target.value)}>
                                                <option value="left">⬅ Left</option>
                                                <option value="center">↔ Center</option>
                                                <option value="right">➡ Right</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <div className="alert alert-info small mb-0">
                                    <strong>💡 Ejemplos:</strong> 450px + 225px + 225px (destacada), 300px + 300px + 300px (igual)
                                </div>
                            </>
                        )}
                    </>
                )

            case 'header_simple':
                return (
                    <>
                        <Form.Group className="mb-3">
                            <Form.Label>Logo Image</Form.Label>
                            <div className="d-flex gap-2">
                                <Form.Control type="text" value={data.logoUrl || ''} onChange={e => handleChange('logoUrl', e.target.value)} placeholder="https://..." />
                                <Button variant="outline-secondary" onClick={() => openMediaFor('logoUrl')}>Select</Button>
                            </div>
                        </Form.Group>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Logo Width</Form.Label>
                                    <PxInput value={data.logoWidth || '150px'} onChange={value => handleChange('logoWidth', value)} placeholder="150" />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Logo Height</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={data.logoHeight || 'auto'}
                                        onChange={e => handleChange('logoHeight', e.target.value)}
                                        placeholder="e.g., auto, 60px"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group className="mb-3">
                            <Form.Label>Background Color</Form.Label>
                            <Form.Control
                                type="color"
                                value={data.backgroundColor || '#ffffff'}
                                onChange={e => handleChange('backgroundColor', e.target.value)}
                            />
                        </Form.Group>
                    </>
                )

            case 'social_links':
                const isVertical = data.socialLayout === 'vertical'
                return (
                    <>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold">Layout Format</Form.Label>
                            <div>
                                <Form.Check inline type="radio" label="Horizontal (Solo Iconos)" checked={!isVertical} onChange={() => handleChange('socialLayout', 'horizontal')} />
                                <Form.Check inline type="radio" label="Vertical (Icono + Texto)" checked={isVertical} onChange={() => handleChange('socialLayout', 'vertical')} />
                            </div>
                        </Form.Group>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Facebook URL</Form.Label>
                                    <Form.Control type="text" value={data.socialFb || ''} onChange={e => handleChange('socialFb', e.target.value)} placeholder="https://facebook.com/..." />
                                </Form.Group>
                                {isVertical && (
                                    <Form.Group className="mb-3">
                                        <Form.Label className="small text-muted">Facebook Text</Form.Label>
                                        <Form.Control type="text" value={data.socialFbText || ''} onChange={e => handleChange('socialFbText', e.target.value)} placeholder="@facebook" />
                                    </Form.Group>
                                )}
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Instagram URL</Form.Label>
                                    <Form.Control type="text" value={data.socialIg || ''} onChange={e => handleChange('socialIg', e.target.value)} placeholder="https://instagram.com/..." />
                                </Form.Group>
                                {isVertical && (
                                    <Form.Group className="mb-3">
                                        <Form.Label className="small text-muted">Instagram Text</Form.Label>
                                        <Form.Control type="text" value={data.socialIgText || ''} onChange={e => handleChange('socialIgText', e.target.value)} placeholder="@instagram" />
                                    </Form.Group>
                                )}
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>LinkedIn URL</Form.Label>
                                    <Form.Control type="text" value={data.socialIn || ''} onChange={e => handleChange('socialIn', e.target.value)} placeholder="https://linkedin.com/..." />
                                </Form.Group>
                                {isVertical && (
                                    <Form.Group className="mb-3">
                                        <Form.Label className="small text-muted">LinkedIn Text</Form.Label>
                                        <Form.Control type="text" value={data.socialInText || ''} onChange={e => handleChange('socialInText', e.target.value)} placeholder="/in/linkedin" />
                                    </Form.Group>
                                )}
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>X (Twitter) URL</Form.Label>
                                    <Form.Control type="text" value={data.socialX || ''} onChange={e => handleChange('socialX', e.target.value)} placeholder="https://x.com/..." />
                                </Form.Group>
                                {isVertical && (
                                    <Form.Group className="mb-3">
                                        <Form.Label className="small text-muted">X Text</Form.Label>
                                        <Form.Control type="text" value={data.socialXText || ''} onChange={e => handleChange('socialXText', e.target.value)} placeholder="@twitter" />
                                    </Form.Group>
                                )}
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>YouTube URL</Form.Label>
                                    <Form.Control type="text" value={data.socialYt || ''} onChange={e => handleChange('socialYt', e.target.value)} placeholder="https://youtube.com/..." />
                                </Form.Group>
                                {isVertical && (
                                    <Form.Group className="mb-3">
                                        <Form.Label className="small text-muted">YouTube Text</Form.Label>
                                        <Form.Control type="text" value={data.socialYtText || ''} onChange={e => handleChange('socialYtText', e.target.value)} placeholder="Channel Name" />
                                    </Form.Group>
                                )}
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>WhatsApp URL</Form.Label>
                                    <Form.Control type="text" value={data.socialWa || ''} onChange={e => handleChange('socialWa', e.target.value)} placeholder="https://wa.me/..." />
                                </Form.Group>
                                {isVertical && (
                                    <Form.Group className="mb-3">
                                        <Form.Label className="small text-muted">WhatsApp Text</Form.Label>
                                        <Form.Control type="text" value={data.socialWaText || ''} onChange={e => handleChange('socialWaText', e.target.value)} placeholder="+1 (555) 123-4567" />
                                    </Form.Group>
                                )}
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Icon Size</Form.Label>
                                    <PxInput value={data.socialSize || '32px'} onChange={value => handleChange('socialSize', value)} placeholder="32" />
                                </Form.Group>
                            </Col>
                            {isVertical && (
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Text Color</Form.Label>
                                        <div className="d-flex gap-2 align-items-center">
                                            <Form.Control
                                                type="color"
                                                value={data.color || '#1d4ed8'}
                                                onChange={e => handleChange('color', e.target.value)}
                                                style={{ width: '60px' }}
                                            />
                                            <Form.Control
                                                type="text"
                                                value={data.color || '#1d4ed8'}
                                                onChange={e => handleChange('color', e.target.value)}
                                                placeholder="#1d4ed8"
                                            />
                                        </div>
                                    </Form.Group>
                                </Col>
                            )}
                        </Row>
                    </>
                )

            case 'contact_card':
                return (
                    <>
                        <h6 className="mb-3 text-muted border-bottom pb-2">Header Configuration</h6>
                        <Form.Group className="mb-3">
                            <Form.Label>Header Text</Form.Label>
                            <Form.Control as="textarea" rows={2} value={data.headerTitle || ''} onChange={e => handleChange('headerTitle', e.target.value)} placeholder="Ej: Complementa tu cobertura y reduce gastos inesperados" />
                        </Form.Group>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Header Background</Form.Label>
                                    <div className="d-flex gap-2">
                                        <Form.Control type="color" value={data.headerBackgroundColor || '#EAEFFE'} onChange={e => handleChange('headerBackgroundColor', e.target.value)} style={{ width: '60px' }} />
                                        <Form.Control type="text" value={data.headerBackgroundColor || '#EAEFFE'} onChange={e => handleChange('headerBackgroundColor', e.target.value)} />
                                    </div>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Header Text Color</Form.Label>
                                    <div className="d-flex gap-2">
                                        <Form.Control type="color" value={data.headerTitleColor || '#1A3F7A'} onChange={e => handleChange('headerTitleColor', e.target.value)} style={{ width: '60px' }} />
                                        <Form.Control type="text" value={data.headerTitleColor || '#1A3F7A'} onChange={e => handleChange('headerTitleColor', e.target.value)} />
                                    </div>
                                </Form.Group>
                            </Col>
                        </Row>

                        <h6 className="mb-3 mt-4 text-muted border-bottom pb-2">Body Configuration</h6>
                        <Form.Group className="mb-3">
                            <Form.Label>Body Title</Form.Label>
                            <Form.Control type="text" value={data.contactTitle || ''} onChange={e => handleChange('contactTitle', e.target.value)} placeholder="Ej: Contáctanos para revisar opciones disponibles" />
                        </Form.Group>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Body Title Color</Form.Label>
                                    <div className="d-flex gap-2">
                                        <Form.Control type="color" value={data.contactTitleColor || '#1f2937'} onChange={e => handleChange('contactTitleColor', e.target.value)} style={{ width: '60px' }} />
                                        <Form.Control type="text" value={data.contactTitleColor || '#1f2937'} onChange={e => handleChange('contactTitleColor', e.target.value)} />
                                    </div>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Social Link Color</Form.Label>
                                    <div className="d-flex gap-2">
                                        <Form.Control type="color" value={data.color || '#1A3F7A'} onChange={e => handleChange('color', e.target.value)} style={{ width: '60px' }} />
                                        <Form.Control type="text" value={data.color || '#1A3F7A'} onChange={e => handleChange('color', e.target.value)} />
                                    </div>
                                </Form.Group>
                            </Col>
                        </Row>

                        <h6 className="mb-3 mt-4 text-muted border-bottom pb-2">Social Contacts</h6>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>WhatsApp URL</Form.Label>
                                    <Form.Control type="text" value={data.socialWa || ''} onChange={e => handleChange('socialWa', e.target.value)} placeholder="https://wa.me/..." />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label className="small text-muted">WhatsApp Text</Form.Label>
                                    <Form.Control type="text" value={data.socialWaText || ''} onChange={e => handleChange('socialWaText', e.target.value)} placeholder="+1 (830) 390-2907" />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Instagram URL</Form.Label>
                                    <Form.Control type="text" value={data.socialIg || ''} onChange={e => handleChange('socialIg', e.target.value)} placeholder="https://instagram.com/..." />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label className="small text-muted">Instagram Text</Form.Label>
                                    <Form.Control type="text" value={data.socialIgText || ''} onChange={e => handleChange('socialIgText', e.target.value)} placeholder="@thefutureinsurance" />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Facebook URL</Form.Label>
                                    <Form.Control type="text" value={data.socialFb || ''} onChange={e => handleChange('socialFb', e.target.value)} placeholder="https://facebook.com/..." />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label className="small text-muted">Facebook Text</Form.Label>
                                    <Form.Control type="text" value={data.socialFbText || ''} onChange={e => handleChange('socialFbText', e.target.value)} placeholder="thefutureinsurance" />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Icon Size</Form.Label>
                                    <PxInput value={data.socialSize || '38px'} onChange={value => handleChange('socialSize', value)} placeholder="38" />
                                </Form.Group>
                            </Col>
                        </Row>
                    </>
                )

            case 'footer_copyright':
                return (
                    <>
                        <Form.Group className="mb-3">
                            <Form.Label>Copyright Text</Form.Label>
                            <Form.Control
                                type="text"
                                value={data.copyrightText || `© ${new Date().getFullYear()} Todos los derechos reservados.`}
                                onChange={e => handleChange('copyrightText', e.target.value)}
                                placeholder="© 2026 Tu Empresa. Todos los derechos reservados."
                            />
                            <Form.Text className="text-muted">
                                Texto que aparecerá como copyright
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Unsubscribe Link Text</Form.Label>
                            <Form.Control
                                type="text"
                                value={data.unsubscribeText || 'Cancelar suscripción'}
                                onChange={e => handleChange('unsubscribeText', e.target.value)}
                                placeholder="Cancelar suscripción"
                            />
                            <Form.Text className="text-muted">
                                El link usará automáticamente la variable {'{'}{'{'} unsubscribe_url {'}'}{'}'}
                            </Form.Text>
                        </Form.Group>

                    </>
                )

            case 'spacer':
                return (
                    <Form.Group className="mb-3">
                        <Form.Label>Spacer Height</Form.Label>
                        <PxInput value={data.spacerHeight || '40px'} onChange={value => handleChange('spacerHeight', value)} placeholder="40" />
                        <Form.Text className="text-muted">
                            Specify the vertical space in pixels (default: 40px)
                        </Form.Text>
                    </Form.Group>
                )

            case 'feature_list':
                return (
                    <>
                        {/* Number of features */}
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold">Number of Features</Form.Label>
                            <div className="d-flex gap-2 mt-1">
                                {[2, 3, 4].map(n => (
                                    <Button
                                        key={n}
                                        size="sm"
                                        variant={(data.featureCount || 3) === n ? 'primary' : 'outline-secondary'}
                                        onClick={() => handleChange('featureCount', n)}
                                        style={{ minWidth: 50 }}
                                    >
                                        {n}
                                    </Button>
                                ))}
                            </div>
                        </Form.Group>

                        {/* Layout */}
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold">Layout</Form.Label>
                            <div className="d-flex gap-2 mt-1">
                                <Button
                                    size="sm"
                                    variant={(data.featureLayout || 'vertical') === 'vertical' ? 'primary' : 'outline-secondary'}
                                    onClick={() => handleChange('featureLayout', 'vertical')}
                                    style={{ minWidth: 110 }}
                                >
                                    Vertical (columns)
                                </Button>
                                <Button
                                    size="sm"
                                    variant={data.featureLayout === 'horizontal' ? 'primary' : 'outline-secondary'}
                                    onClick={() => handleChange('featureLayout', 'horizontal')}
                                    style={{ minWidth: 110 }}
                                >
                                    Horizontal (rows)
                                </Button>
                            </div>
                        </Form.Group>

                        {/* Icon color + size */}
                        <Row>
                            <Col md={7}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Icon Color</Form.Label>
                                    <div className="d-flex gap-2 align-items-center">
                                        <Form.Control
                                            type="color"
                                            value={data.featureIconColor || '#007bff'}
                                            onChange={e => handleChange('featureIconColor', e.target.value)}
                                            style={{ width: '50px' }}
                                        />
                                        <Form.Control
                                            type="text"
                                            value={data.featureIconColor || '#007bff'}
                                            onChange={e => handleChange('featureIconColor', e.target.value)}
                                            placeholder="#007bff"
                                        />
                                    </div>
                                </Form.Group>
                            </Col>
                            <Col md={5}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Icon Size</Form.Label>
                                    <div className="d-flex gap-1">
                                        {[['S','24px'],['M','36px'],['L','48px'],['XL','60px']].map(([label, val]) => (
                                            <Button
                                                key={val}
                                                size="sm"
                                                variant={(data.featureIconSize || '36px') === val ? 'primary' : 'outline-secondary'}
                                                onClick={() => handleChange('featureIconSize', val)}
                                                style={{ flex: 1 }}
                                            >
                                                {label}
                                            </Button>
                                        ))}
                                    </div>
                                </Form.Group>
                            </Col>
                        </Row>

                        <hr className="my-3" />

                        {/* Feature rows */}
                        {[
                            { n: 1, iconKey: 'feature1Icon' as const, titleKey: 'feature1Title' as const, textKey: 'feature1Text' as const, always: true },
                            { n: 2, iconKey: 'feature2Icon' as const, titleKey: 'feature2Title' as const, textKey: 'feature2Text' as const, always: true },
                            { n: 3, iconKey: 'feature3Icon' as const, titleKey: 'feature3Title' as const, textKey: 'feature3Text' as const, always: false },
                            { n: 4, iconKey: 'feature4Icon' as const, titleKey: 'feature4Title' as const, textKey: 'feature4Text' as const, always: false },
                        ].filter(f => f.always || (data.featureCount || 3) >= f.n).map(f => (
                            <div key={f.n}>
                                <h6 className="mb-2 text-muted small fw-bold text-uppercase">Feature {f.n}</h6>
                                <div className="d-flex gap-3 align-items-start mb-2">
                                    <div>
                                        <Form.Label className="small d-block mb-1">Icon</Form.Label>
                                        <EmojiPicker
                                            value={data[f.iconKey] || ''}
                                            onChange={val => handleChange(f.iconKey, val)}
                                        />
                                    </div>
                                    <div className="flex-grow-1">
                                        <Form.Group className="mb-2">
                                            <Form.Label className="small">Title</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={data[f.titleKey] || ''}
                                                onChange={e => handleChange(f.titleKey, e.target.value)}
                                                placeholder="Feature title"
                                            />
                                        </Form.Group>
                                        <Form.Group>
                                            <Form.Label className="small">Description</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                value={data[f.textKey] || ''}
                                                onChange={e => handleChange(f.textKey, e.target.value)}
                                                placeholder="Short description..."
                                            />
                                        </Form.Group>
                                    </div>
                                </div>
                                {f.n < (data.featureCount || 3) && <hr className="my-3" />}
                            </div>
                        ))}
                    </>
                )

            case 'card_grid':
                return (
                    <>
                        {/* Card count */}
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold">Number of Cards</Form.Label>
                            <div className="d-flex gap-2 mt-1">
                                {[2, 3].map(n => (
                                    <Button
                                        key={n}
                                        size="sm"
                                        variant={(data.cardCount || 2) === n ? 'primary' : 'outline-secondary'}
                                        onClick={() => handleChange('cardCount', n)}
                                        style={{ minWidth: 60 }}
                                    >
                                        {n} Cards
                                    </Button>
                                ))}
                            </div>
                        </Form.Group>

                        {/* Card Image Size */}
                        <Form.Group className="mb-3">
                            <Form.Label>Image Height</Form.Label>
                            <Form.Control
                                type="text"
                                value={data.imageHeight || ''}
                                onChange={e => handleChange('imageHeight', e.target.value)}
                                placeholder="e.g., 200px, 150px, auto"
                            />
                            <Form.Text className="text-muted">
                                Dejar vacío para tamaño automático (ancho 100%).
                            </Form.Text>
                        </Form.Group>

                        {/* Button color */}
                        <Form.Group className="mb-3">
                            <Form.Label>Button Color</Form.Label>
                            <div className="d-flex gap-2 align-items-center">
                                <Form.Control
                                    type="color"
                                    value={data.buttonColor || '#007bff'}
                                    onChange={e => handleChange('buttonColor', e.target.value)}
                                    style={{ width: '60px' }}
                                />
                                <Form.Control
                                    type="text"
                                    value={data.buttonColor || '#007bff'}
                                    onChange={e => handleChange('buttonColor', e.target.value)}
                                    placeholder="#007bff"
                                />
                            </div>
                        </Form.Group>

                        <hr className="my-3" />

                        {/* Card 1 */}
                        <h6 className="mb-2">Card 1</h6>
                        <Form.Group className="mb-3">
                            <Form.Label className="small">Image URL</Form.Label>
                            <div className="d-flex gap-2">
                                <Form.Control
                                    type="text"
                                    value={data.card1Image || ''}
                                    onChange={e => handleChange('card1Image', e.target.value)}
                                    placeholder="https://..."
                                />
                                <Button variant="outline-secondary" onClick={() => openMediaFor('card1Image')}>Select</Button>
                            </div>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="small">Title</Form.Label>
                            <Form.Control type="text" value={data.card1Title || ''} onChange={e => handleChange('card1Title', e.target.value)} placeholder="Card title" />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="small">Text</Form.Label>
                            <Form.Control as="textarea" rows={2} value={data.card1Text || ''} onChange={e => handleChange('card1Text', e.target.value)} placeholder="Card description..." />
                        </Form.Group>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="small">Button Text</Form.Label>
                                    <Form.Control type="text" value={data.card1ButtonText || ''} onChange={e => handleChange('card1ButtonText', e.target.value)} placeholder="Learn more" />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="small">Button URL</Form.Label>
                                    <Form.Control type="text" value={data.card1ButtonUrl || ''} onChange={e => handleChange('card1ButtonUrl', e.target.value)} placeholder="https://..." />
                                </Form.Group>
                            </Col>
                        </Row>

                        <hr className="my-3" />

                        {/* Card 2 */}
                        <h6 className="mb-2">Card 2</h6>
                        <Form.Group className="mb-3">
                            <Form.Label className="small">Image URL</Form.Label>
                            <div className="d-flex gap-2">
                                <Form.Control
                                    type="text"
                                    value={data.card2Image || ''}
                                    onChange={e => handleChange('card2Image', e.target.value)}
                                    placeholder="https://..."
                                />
                                <Button variant="outline-secondary" onClick={() => openMediaFor('card2Image')}>Select</Button>
                            </div>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="small">Title</Form.Label>
                            <Form.Control type="text" value={data.card2Title || ''} onChange={e => handleChange('card2Title', e.target.value)} placeholder="Card title" />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="small">Text</Form.Label>
                            <Form.Control as="textarea" rows={2} value={data.card2Text || ''} onChange={e => handleChange('card2Text', e.target.value)} placeholder="Card description..." />
                        </Form.Group>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="small">Button Text</Form.Label>
                                    <Form.Control type="text" value={data.card2ButtonText || ''} onChange={e => handleChange('card2ButtonText', e.target.value)} placeholder="Learn more" />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="small">Button URL</Form.Label>
                                    <Form.Control type="text" value={data.card2ButtonUrl || ''} onChange={e => handleChange('card2ButtonUrl', e.target.value)} placeholder="https://..." />
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* Card 3 - shown when cardCount === 3 */}
                        {(data.cardCount || 2) >= 3 && (
                            <>
                                <hr className="my-3" />
                                <h6 className="mb-2">Card 3</h6>
                                <Form.Group className="mb-3">
                                    <Form.Label className="small">Image URL</Form.Label>
                                    <div className="d-flex gap-2">
                                        <Form.Control
                                            type="text"
                                            value={data.card3Image || ''}
                                            onChange={e => handleChange('card3Image', e.target.value)}
                                            placeholder="https://..."
                                        />
                                        <Button variant="outline-secondary" onClick={() => openMediaFor('card3Image')}>Select</Button>
                                    </div>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label className="small">Title</Form.Label>
                                    <Form.Control type="text" value={data.card3Title || ''} onChange={e => handleChange('card3Title', e.target.value)} placeholder="Card title" />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label className="small">Text</Form.Label>
                                    <Form.Control as="textarea" rows={2} value={data.card3Text || ''} onChange={e => handleChange('card3Text', e.target.value)} placeholder="Card description..." />
                                </Form.Group>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="small">Button Text</Form.Label>
                                            <Form.Control type="text" value={data.card3ButtonText || ''} onChange={e => handleChange('card3ButtonText', e.target.value)} placeholder="Learn more" />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="small">Button URL</Form.Label>
                                            <Form.Control type="text" value={data.card3ButtonUrl || ''} onChange={e => handleChange('card3ButtonUrl', e.target.value)} placeholder="https://..." />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </>
                        )}
                    </>
                )

            case 'testimonial':
                return (
                    <>
                        {/* Style selector */}
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold">Style</Form.Label>
                            <div className="d-flex gap-2 mt-1">
                                <Button
                                    size="sm"
                                    variant={(data.quoteStyle || 'simple') === 'simple' ? 'primary' : 'outline-secondary'}
                                    onClick={() => handleChange('quoteStyle', 'simple')}
                                    style={{ minWidth: 80 }}
                                >
                                    Simple
                                </Button>
                                <Button
                                    size="sm"
                                    variant={data.quoteStyle === 'card' ? 'primary' : 'outline-secondary'}
                                    onClick={() => handleChange('quoteStyle', 'card')}
                                    style={{ minWidth: 80 }}
                                >
                                    Card
                                </Button>
                            </div>
                        </Form.Group>

                        {/* Quote text */}
                        <Form.Group className="mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <Form.Label className="mb-0">Quote Text</Form.Label>
                                <Button variant="outline-secondary" size="sm" onClick={() => openContentEditor('quoteText', 'Quote Text')}>
                                    <i className="bi bi-pencil-square me-1"></i>
                                    Open Rich Editor
                                </Button>
                            </div>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={data.quoteText || ''}
                                onChange={e => handleChange('quoteText', e.target.value)}
                                placeholder="Enter the testimonial quote..."
                            />
                        </Form.Group>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Author Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={data.quoteAuthor || ''}
                                        onChange={e => handleChange('quoteAuthor', e.target.value)}
                                        placeholder="Jane Doe"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Role / Company</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={data.quoteRole || ''}
                                        onChange={e => handleChange('quoteRole', e.target.value)}
                                        placeholder="CEO, Acme Inc."
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* Avatar URL */}
                        <Form.Group className="mb-3">
                            <Form.Label>Avatar Image (optional)</Form.Label>
                            <div className="d-flex gap-2">
                                <Form.Control
                                    type="text"
                                    value={data.quoteAvatarUrl || ''}
                                    onChange={e => handleChange('quoteAvatarUrl', e.target.value)}
                                    placeholder="https://..."
                                />
                                <Button variant="outline-secondary" onClick={() => openMediaFor('quoteAvatarUrl')}>Select</Button>
                            </div>
                        </Form.Group>

                        {/* Accent color */}
                        <Form.Group className="mb-3">
                            <Form.Label>Accent Color</Form.Label>
                            <div className="d-flex gap-2 align-items-center">
                                <Form.Control
                                    type="color"
                                    value={data.buttonColor || '#007bff'}
                                    onChange={e => handleChange('buttonColor', e.target.value)}
                                    style={{ width: '60px' }}
                                />
                                <Form.Control
                                    type="text"
                                    value={data.buttonColor || '#007bff'}
                                    onChange={e => handleChange('buttonColor', e.target.value)}
                                    placeholder="#007bff"
                                />
                            </div>
                            <Form.Text className="text-muted">Used for the card border or decorative elements</Form.Text>
                        </Form.Group>
                    </>
                )

            default:
                return null
        }
    }

    const renderStyleFields = () => (
        <>
            <Row>
                <Col md={12}>
                    <Form.Group className="mb-3">
                        <Form.Label>Block Alignment</Form.Label>
                        <div className="d-flex gap-3">
                            {['left', 'center', 'right'].map(align => (
                                <Form.Check
                                    key={align}
                                    type="radio"
                                    id={`style-align-${align}`}
                                    label={align.charAt(0).toUpperCase() + align.slice(1)}
                                    checked={data.align === align}
                                    onChange={() => handleChange('align', align)}
                                />
                            ))}
                        </div>
                    </Form.Group>
                </Col>
            </Row>

            <Form.Group className="mb-3">
                <Form.Label>Block Background Color</Form.Label>
                <div className="d-flex gap-2 align-items-center">
                    <HexColorInput
                        value={data.backgroundColor === 'transparent' ? '' : (data.backgroundColor || '#FFFFFF')}
                        onChange={value => handleChange('backgroundColor', value)}
                        fallback='#FFFFFF'
                    />
                    <Button variant="outline-secondary" size="sm" onClick={() => handleChange('backgroundColor', 'transparent')}>Transparent</Button>
                </div>
            </Form.Group>

            <hr />
            <h6>Spacing (Padding)</h6>
            <Row>
                <Col md={6}>
                    <Form.Group className="mb-2">
                        <Form.Label className="small">Top</Form.Label>
                        <PxInput size="sm" value={data.paddingTop || '20px'} onChange={value => handleChange('paddingTop', value)} placeholder="20" />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-2">
                        <Form.Label className="small">Bottom</Form.Label>
                        <PxInput size="sm" value={data.paddingBottom || '20px'} onChange={value => handleChange('paddingBottom', value)} placeholder="20" />
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col md={6}>
                    <Form.Group className="mb-2">
                        <Form.Label className="small">Left</Form.Label>
                        <PxInput size="sm" value={data.paddingLeft || '20px'} onChange={value => handleChange('paddingLeft', value)} placeholder="20" />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-2">
                        <Form.Label className="small">Right</Form.Label>
                        <PxInput size="sm" value={data.paddingRight || '20px'} onChange={value => handleChange('paddingRight', value)} placeholder="20" />
                    </Form.Group>
                </Col>
            </Row>

            <hr />
            <h6>Borders</h6>
            <Row>
                <Col md={6}>
                    <Form.Group className="mb-2">
                        <Form.Label className="small">Width</Form.Label>
                        <PxInput size="sm" value={data.borderWidth || '0px'} onChange={value => handleChange('borderWidth', value)} placeholder="0" />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-2">
                        <Form.Label className="small">Style</Form.Label>
                        <Form.Select size="sm" value={data.borderStyle || 'solid'} onChange={e => handleChange('borderStyle', e.target.value)}>
                            <option value="solid">Solid</option>
                            <option value="dashed">Dashed</option>
                            <option value="dotted">Dotted</option>
                        </Form.Select>
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col md={6}>
                    <Form.Group className="mb-2">
                        <Form.Label className="small">Border Color</Form.Label>
                        <HexColorInput
                            value={data.borderColor || '#DDDDDD'}
                            onChange={value => handleChange('borderColor', value)}
                            fallback='#DDDDDD'
                            size='sm'
                        />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-2">
                        <Form.Label className="small">Border Radius</Form.Label>
                        <PxInput size="sm" value={data.borderRadius || '0px'} onChange={value => handleChange('borderRadius', value)} placeholder="0" />
                    </Form.Group>
                </Col>
            </Row>
        </>
    )

    return (
        <>
            <Modal show={show} onHide={onHide} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Configure {block?.label}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Tabs
                        activeKey={activeTab}
                        onSelect={(k) => setActiveTab(k || 'content')}
                        className="mb-3"
                    >
                        <Tab eventKey="content" title="Content">
                            <div className="py-2">
                                <Form>
                                    {renderFields()}
                                </Form>
                            </div>
                        </Tab>
                        <Tab eventKey="style" title="Style & Design">
                            <div className="py-2">
                                <Form>
                                    {renderStyleFields()}
                                </Form>
                            </div>
                        </Tab>
                    </Tabs>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide}>Cancel</Button>
                    <Button variant="primary" onClick={() => onConfirm(data)}>Save Changes</Button>
                </Modal.Footer>
            </Modal>

            {showMediaManager && (
                <MediaManagerModal
                    isOpen={showMediaManager}
                    onClose={() => setShowMediaManager(false)}
                    onSelect={handleMediaSelect}
                />
            )}

            <Modal
                show={contentEditorField !== null}
                onHide={() => setContentEditorField(null)}
                size="xl"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Edit {contentEditorLabel}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {contentEditorField && (
                        <HTMLEditor
                            value={String(data[contentEditorField] || '')}
                            onChange={(value) => handleChange(contentEditorField, value)}
                            height={420}
                            mode='embedded'
                        />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setContentEditorField(null)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}
